import jsPDF from 'jspdf';
import { Insight } from './analyticsInsights';

// Brand colors from Crisci design system
export const BRAND_COLORS = {
  primary: '#0F1B2D',      // Navy
  accent: '#C7A14A',       // Gold
  surface: '#F7F8FA',      // Light gray
  border: '#E5E7EB',       // Border gray
  muted: '#6B7280',        // Text muted
  white: '#FFFFFF',
  teal: '#2B7A78',         // Teal from logo
};

export interface PDFExportOptions {
  // Survey Info
  surveyTitle: string;
  surveyDescription?: string;

  // Sections to Include
  includeSummary: boolean;
  includeOverview: boolean;
  includeCharts: boolean;
  includeDetailedBreakdown: boolean;
  includeRawData: boolean;
  includeInsights: boolean;  // AI-generated insights

  // Detail Level
  showDualDenominators: boolean;
  showConditionalContext: boolean;
  showPercentages: boolean;
  showCounts: boolean;

  // Filters
  selectedSections?: string[];  // Section IDs to include
  selectedQuestions?: string[]; // Question IDs to include

  // Branding
  includeLogo: boolean;
  includeFooter: boolean;
  reportTitle?: string;
  reportSubtitle?: string;
  generatedBy?: string;
  generatedDate?: Date;

  // Layout Options
  lineSpacing?: 'compact' | 'normal' | 'spacious'; // Line spacing density
  pageMargins?: 'narrow' | 'normal' | 'wide'; // Page margin width
}

export interface AnalyticsData {
  totalResponses: number;
  completionRate: number;
  avgCompletionTime: number;
  lastResponseDate?: Date;
  sections: SectionAnalytics[];
}

export interface SectionAnalytics {
  sectionId: string;
  sectionTitle: string;
  questions: QuestionAnalytics[];
}

export interface QuestionAnalytics {
  questionId: string;
  questionTitle: string;
  questionType: string;
  responseCount: number;
  totalSurveyRespondents: number;
  eligibleRespondents?: number;       // Who could see this question (for conditional)
  skipCount?: number;                  // Who saw but didn't answer
  notShownCount?: number;              // Conditional - didn't see it
  conditionalContext?: {
    isConditional: boolean;
    eligibleCount: number;
    description: string;
  };
  distribution?: Record<string, {
    count: number;
    percentage: number;
    percentageOfTotal: number;
  }>;
  numericStats?: {
    min: number;
    max: number;
    average: number;
    median: number;
    stdDev?: number;                   // Standard deviation
    mode?: number;                     // Most common value
  };
  textResponses?: string[];
}

/**
 * Main PDF Export Class
 * Handles professional PDF generation with modular sections
 */
export class AnalyticsPDFExporter {
  private pdf: jsPDF;
  private currentY: number = 0;
  private pageHeight: number = 0;
  private pageWidth: number = 0;
  private margin: number = 12; // Default to narrow margins
  private lineHeight: number = 5.5; // Default to compact spacing

  constructor(options?: PDFExportOptions) {
    // A4 size, portrait
    this.pdf = new jsPDF('p', 'mm', 'a4');
    this.pageHeight = this.pdf.internal.pageSize.height;
    this.pageWidth = this.pdf.internal.pageSize.width;

    // Set margins based on options
    if (options?.pageMargins === 'wide') {
      this.margin = 20;
    } else if (options?.pageMargins === 'normal') {
      this.margin = 15;
    } else {
      this.margin = 12; // narrow (default)
    }

    // Set line spacing based on options
    if (options?.lineSpacing === 'spacious') {
      this.lineHeight = 7;
    } else if (options?.lineSpacing === 'normal') {
      this.lineHeight = 6;
    } else {
      this.lineHeight = 5.5; // compact (default)
    }

    this.currentY = this.margin;
  }

  /**
   * Generate complete PDF report
   */
  async generateReport(
    data: AnalyticsData,
    options: PDFExportOptions,
    insights?: Insight[]
  ): Promise<Blob> {
    try {
      // Add cover page
      if (options.includeLogo) {
        await this.addCoverPage(options);
      }

      // Add executive summary
      if (options.includeSummary) {
        this.addExecutiveSummary(data, options);
      }

      // Add AI insights section
      if (options.includeInsights && insights && insights.length > 0) {
        this.addInsightsSection(insights);
      }

      // Add overview section
      if (options.includeOverview) {
        this.addOverviewSection(data);
      }

      // Add question-by-question analytics
      if (options.includeCharts || options.includeDetailedBreakdown) {
        await this.addQuestionAnalytics(data, options);
      }

      // Add footer to all pages
      if (options.includeFooter) {
        this.addFooterToAllPages(options);
      }

      // Return as blob
      return this.pdf.output('blob');
    } catch (error) {
      console.error('[PDFExporter] Error generating PDF:', error);
      throw error;
    }
  }

  /**
   * Add professional cover page with branding
   */
  private async addCoverPage(options: PDFExportOptions): Promise<void> {
    // Background accent bar
    this.pdf.setFillColor(BRAND_COLORS.teal);
    this.pdf.rect(0, 0, this.pageWidth, 60, 'F');

    // Logo (if available)
    try {
      const logoImg = await this.loadImage('/criscireport.png');
      const logoWidth = 50;
      const logoHeight = 50;
      const logoX = (this.pageWidth - logoWidth) / 2;
      this.pdf.addImage(logoImg, 'PNG', logoX, 70, logoWidth, logoHeight);
    } catch (err) {
      console.warn('[PDFExporter] Could not load logo');
    }

    // Report title
    this.pdf.setFont('helvetica', 'bold');
    this.pdf.setFontSize(28);
    this.pdf.setTextColor(BRAND_COLORS.primary);
    const title = options.reportTitle || 'Analytics Report';
    this.pdf.text(title, this.pageWidth / 2, 140, { align: 'center' });

    // Survey title
    this.pdf.setFontSize(18);
    this.pdf.setTextColor(BRAND_COLORS.muted);
    this.pdf.text(options.surveyTitle, this.pageWidth / 2, 155, { align: 'center' });

    // Subtitle
    if (options.reportSubtitle) {
      this.pdf.setFontSize(14);
      this.pdf.text(options.reportSubtitle, this.pageWidth / 2, 165, { align: 'center' });
    }

    // Date and metadata
    this.pdf.setFontSize(10);
    this.pdf.setTextColor(BRAND_COLORS.muted);
    const date = options.generatedDate || new Date();
    const dateStr = date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    this.pdf.text(`Generated: ${dateStr}`, this.pageWidth / 2, 250, { align: 'center' });

    if (options.generatedBy) {
      this.pdf.text(`By: ${options.generatedBy}`, this.pageWidth / 2, 257, { align: 'center' });
    }

    // Crisci Create branding at bottom
    this.pdf.setFontSize(12);
    this.pdf.setTextColor(BRAND_COLORS.accent);
    this.pdf.text('Crisci Create', this.pageWidth / 2, 270, { align: 'center' });
    this.pdf.setFontSize(9);
    this.pdf.text('Pharmacy Research Platform', this.pageWidth / 2, 276, { align: 'center' });

    // Start new page for content
    this.pdf.addPage();
    this.currentY = this.margin;
  }

  /**
   * Add executive summary section
   */
  private addExecutiveSummary(data: AnalyticsData, options: PDFExportOptions): void {
    this.addSectionHeader('Executive Summary');

    // Survey overview box
    this.addInfoBox([
      { label: 'Survey', value: options.surveyTitle },
      { label: 'Total Responses', value: data.totalResponses.toString() },
      { label: 'Completion Rate', value: `${data.completionRate.toFixed(1)}%` },
      { label: 'Avg Completion Time', value: `${Math.round(data.avgCompletionTime / 60)} minutes` },
    ]);

    this.currentY += 10;

    // Key findings - Enhanced typography
    this.pdf.setFont('helvetica', 'bold');
    this.pdf.setFontSize(14);
    this.pdf.setTextColor(BRAND_COLORS.primary);
    this.pdf.text('Key Findings', this.margin, this.currentY);
    this.currentY += this.lineHeight + 3;

    // Findings with larger, darker text
    this.pdf.setFont('helvetica', 'normal');
    this.pdf.setFontSize(11);
    this.pdf.setTextColor('#1F2937'); // Darker gray instead of muted

    const findings = [
      `• This report presents analytics from ${data.totalResponses} survey responses.`,
      `• The survey achieved a ${data.completionRate.toFixed(1)}% completion rate.`,
      `• Analysis covers ${data.sections.reduce((sum, s) => sum + s.questions.length, 0)} questions across ${data.sections.length} sections.`
    ];

    findings.forEach(finding => {
      const lines = this.pdf.splitTextToSize(finding, this.pageWidth - 2 * this.margin);
      this.pdf.text(lines, this.margin, this.currentY);
      this.currentY += lines.length * (this.lineHeight + 1);
    });

    this.currentY += 5;
  }

  /**
   * Add AI-generated insights section
   */
  private addInsightsSection(insights: Insight[]): void {
    this.checkPageBreak(60);
    this.addSectionHeader('AI-Generated Insights');

    const highPriority = insights.filter(i => i.priority === 'high');
    const mediumPriority = insights.filter(i => i.priority === 'medium');
    const lowPriority = insights.filter(i => i.priority === 'low');

    // High priority insights (Action Required)
    if (highPriority.length > 0) {
      this.checkPageBreak(30);

      // Section header with icon
      this.pdf.setFillColor('#FEF2F2'); // Light red background
      const sectionStartY = this.currentY;
      this.pdf.rect(this.margin, sectionStartY, this.pageWidth - 2 * this.margin, 10, 'F');

      // Red accent bar
      this.pdf.setFillColor('#DC2626');
      this.pdf.rect(this.margin, sectionStartY, 3, 10, 'F');

      this.pdf.setFont('helvetica', 'bold');
      this.pdf.setFontSize(11);
      this.pdf.setTextColor('#DC2626');
      this.pdf.text('! ACTION REQUIRED', this.margin + 6, sectionStartY + 6.5);
      this.currentY += 14;

      // High priority insight items
      highPriority.forEach(insight => {
        this.checkPageBreak(15);
        this.addInsightItem(insight, '#DC2626', '#FEF2F2');
      });

      this.currentY += 5;
    }

    // Medium priority insights (Key Findings)
    if (mediumPriority.length > 0 || lowPriority.length > 0) {
      this.checkPageBreak(30);

      // Section header
      this.pdf.setFillColor('#EFF6FF'); // Light blue background
      const sectionStartY = this.currentY;
      this.pdf.rect(this.margin, sectionStartY, this.pageWidth - 2 * this.margin, 10, 'F');

      // Blue accent bar
      this.pdf.setFillColor('#264372');
      this.pdf.rect(this.margin, sectionStartY, 3, 10, 'F');

      this.pdf.setFont('helvetica', 'bold');
      this.pdf.setFontSize(11);
      this.pdf.setTextColor('#264372');
      this.pdf.text('KEY FINDINGS', this.margin + 6, sectionStartY + 6.5);
      this.currentY += 14;

      // Medium priority items
      mediumPriority.forEach(insight => {
        this.checkPageBreak(15);
        this.addInsightItem(insight, '#F59E0B', '#FFFBEB');
      });

      // Low priority items
      lowPriority.slice(0, 5).forEach(insight => { // Limit to first 5 low priority
        this.checkPageBreak(15);
        this.addInsightItem(insight, '#264372', '#F8FAFC');
      });

      if (lowPriority.length > 5) {
        this.pdf.setFont('helvetica', 'italic');
        this.pdf.setFontSize(8);
        this.pdf.setTextColor(BRAND_COLORS.muted);
        this.pdf.text(`+ ${lowPriority.length - 5} additional low-priority insights not shown`, this.margin + 10, this.currentY);
        this.currentY += 6;
      }
    }

    // Summary line
    this.currentY += 3;
    this.pdf.setFont('helvetica', 'normal');
    this.pdf.setFontSize(9);
    this.pdf.setTextColor(BRAND_COLORS.muted);
    this.pdf.text(`${insights.length} total insights generated from survey data`, this.margin, this.currentY);
    this.currentY += 10;
  }

  /**
   * Add a single insight item
   */
  private addInsightItem(insight: Insight, accentColor: string, _bgColor: string): void {
    const contentWidth = this.pageWidth - 2 * this.margin - 10;

    // Title and description
    this.pdf.setFont('helvetica', 'bold');
    this.pdf.setFontSize(9);
    this.pdf.setTextColor(accentColor);
    const titleText = `• ${insight.title}:`;
    this.pdf.text(titleText, this.margin + 5, this.currentY + 4);

    // Calculate title width for inline description
    const titleWidth = this.pdf.getTextWidth(titleText);

    this.pdf.setFont('helvetica', 'normal');
    this.pdf.setFontSize(9);
    this.pdf.setTextColor(BRAND_COLORS.primary);

    // Description text - may wrap to multiple lines
    const descriptionWidth = contentWidth - titleWidth - 8;
    const descLines = this.pdf.splitTextToSize(insight.description, descriptionWidth);

    if (descLines.length === 1) {
      // Single line - put inline with title
      this.pdf.text(descLines[0], this.margin + 7 + titleWidth, this.currentY + 4);
      this.currentY += 8;
    } else {
      // Multi-line - wrap description below title
      this.currentY += 8;
      const wrappedDesc = this.pdf.splitTextToSize(insight.description, contentWidth - 10);
      this.pdf.text(wrappedDesc, this.margin + 10, this.currentY);
      this.currentY += wrappedDesc.length * 4.5;
    }

    // Question reference if available
    if (insight.questionTitle) {
      this.pdf.setFont('helvetica', 'italic');
      this.pdf.setFontSize(7);
      this.pdf.setTextColor(BRAND_COLORS.muted);
      this.pdf.text(`Re: "${this.truncateText(insight.questionTitle, 60)}"`, this.margin + 10, this.currentY);
      this.currentY += 5;
    }

    this.currentY += 2;
  }

  /**
   * Add overview section with high-level stats and detailed question tables
   */
  private addOverviewSection(data: AnalyticsData): void {
    this.checkPageBreak(60);
    this.addSectionHeader('Survey Overview');

    data.sections.forEach((section, index) => {
      this.checkPageBreak(40);

      // Section title
      this.pdf.setFont('helvetica', 'bold');
      this.pdf.setFontSize(12);
      this.pdf.setTextColor(BRAND_COLORS.primary);
      this.pdf.text(`${index + 1}. ${section.sectionTitle}`, this.margin, this.currentY);
      this.currentY += this.lineHeight + 2;

      this.pdf.setFont('helvetica', 'normal');
      this.pdf.setFontSize(10);
      this.pdf.setTextColor(BRAND_COLORS.muted);
      this.pdf.text(`${section.questions.length} questions`, this.margin + 5, this.currentY);
      this.currentY += this.lineHeight + 4;

      // Add detailed question table for this section
      this.addQuestionTable(section);

      this.currentY += 8;
    });

    this.currentY += 5;
  }

  /**
   * Add detailed question table showing question types and options
   */
  private addQuestionTable(section: SectionAnalytics): void {
    section.questions.forEach((question, qIndex) => {
      this.checkPageBreak(35);

      // Question number and title
      this.pdf.setFont('helvetica', 'bold');
      this.pdf.setFontSize(9);
      this.pdf.setTextColor(BRAND_COLORS.primary);
      const questionLabel = `Q${qIndex + 1}:`;
      this.pdf.text(questionLabel, this.margin + 8, this.currentY);

      // Question title (wrapped)
      this.pdf.setFont('helvetica', 'normal');
      const titleLines = this.pdf.splitTextToSize(question.questionTitle, this.pageWidth - 2 * this.margin - 25);
      this.pdf.text(titleLines, this.margin + 20, this.currentY);
      this.currentY += Math.max(titleLines.length * 5, 5) + 2;

      // Question type and response info
      this.pdf.setFontSize(8);
      this.pdf.setTextColor(BRAND_COLORS.muted);
      this.pdf.text(`Type: ${this.formatQuestionType(question.questionType)}`, this.margin + 20, this.currentY);
      this.currentY += 4;

      // Show available options for choice-based questions
      if (question.distribution) {
        const options = Object.keys(question.distribution);
        if (options.length > 0) {
          this.pdf.text(`Options: ${options.map(o => this.formatLabel(o)).join(', ')}`, this.margin + 20, this.currentY);
          this.currentY += 4;
        }
      }

      // Quick statistics summary
      this.addQuickStatsSummary(question);

      this.currentY += 3;
    });
  }

  /**
   * Add quick statistics summary for a question
   */
  private addQuickStatsSummary(question: QuestionAnalytics): void {
    this.pdf.setFontSize(8);
    this.pdf.setFont('helvetica', 'bold');
    this.pdf.setTextColor(BRAND_COLORS.accent);

    // Calculate response rate
    const eligible = question.eligibleRespondents ?? question.totalSurveyRespondents;
    const responseRate = eligible > 0 ? ((question.responseCount / eligible) * 100).toFixed(0) : '0';

    // Show response count with context
    let responseText = `${question.responseCount} of ${eligible} responded (${responseRate}%)`;
    if (question.skipCount && question.skipCount > 0) {
      responseText += ` · ${question.skipCount} skipped`;
    }
    this.pdf.text(responseText, this.margin + 20, this.currentY);
    this.currentY += 4;

    // Distribution breakdown for choice-based questions
    if (question.distribution) {
      const entries = Object.entries(question.distribution).sort(([, a], [, b]) => b.count - a.count);

      this.pdf.setFont('helvetica', 'normal');
      this.pdf.setFontSize(7);
      this.pdf.setTextColor(BRAND_COLORS.muted);

      entries.slice(0, 3).forEach(([option, data]) => {
        const formattedOption = this.formatLabel(option);
        const text = `  • ${formattedOption}: ${data.count} (${data.percentage.toFixed(1)}%)`;
        this.pdf.text(text, this.margin + 22, this.currentY);
        this.currentY += 3.5;
      });

      if (entries.length > 3) {
        this.pdf.setFont('helvetica', 'italic');
        this.pdf.text(`  ... and ${entries.length - 3} more options`, this.margin + 22, this.currentY);
        this.currentY += 3.5;
      }
    }
    // Numeric statistics
    else if (question.numericStats) {
      this.pdf.setFont('helvetica', 'normal');
      this.pdf.setFontSize(7);
      this.pdf.setTextColor(BRAND_COLORS.muted);
      this.pdf.text(`  • Average: ${question.numericStats.average.toFixed(2)} | Range: ${question.numericStats.min} - ${question.numericStats.max}`, this.margin + 22, this.currentY);
      this.currentY += 3.5;
    }
    // Text responses
    else if (question.textResponses && question.textResponses.length > 0) {
      this.pdf.setFont('helvetica', 'normal');
      this.pdf.setFontSize(7);
      this.pdf.setTextColor(BRAND_COLORS.muted);
      this.pdf.text(`  • ${question.textResponses.length} text responses provided`, this.margin + 22, this.currentY);
      this.currentY += 3.5;
    }
  }

  /**
   * Format question type for display
   */
  private formatQuestionType(type: string): string {
    const typeMap: Record<string, string> = {
      'text': 'Text Input',
      'textarea': 'Long Text',
      'number': 'Number',
      'radio': 'Single Choice',
      'checkbox': 'Multiple Choice',
      'select': 'Dropdown',
      'rating': 'Rating',
      'date': 'Date',
      'email': 'Email',
      'tel': 'Phone',
      'dosing_schedule': 'Dosing Schedule',
      'measurement': 'Dose / Strength'
    };
    return typeMap[type] || type;
  }

  /**
   * Add detailed question-by-question analytics
   */
  private async addQuestionAnalytics(
    data: AnalyticsData,
    options: PDFExportOptions
  ): Promise<void> {
    // Start Detailed Analytics section
    this.checkPageBreak(40);
    this.addSectionHeader('Detailed Analytics');

    let isFirstSection = true;

    for (const section of data.sections) {
      // Skip if section not selected
      if (options.selectedSections && !options.selectedSections.includes(section.sectionId)) {
        continue;
      }

      // Start a new page for each section (except the first one, which follows "Detailed Analytics")
      if (!isFirstSection) {
        this.pdf.addPage();
        this.currentY = this.margin;
      }
      isFirstSection = false;

      this.addSubheader(section.sectionTitle);

      for (const question of section.questions) {
        // Skip if question not selected
        if (options.selectedQuestions && !options.selectedQuestions.includes(question.questionId)) {
          continue;
        }

        await this.addQuestionCard(question, options);
      }
    }
  }

  /**
   * Add individual question card with analytics
   */
  private async addQuestionCard(
    question: QuestionAnalytics,
    options: PDFExportOptions
  ): Promise<void> {
    // Estimate minimum required space for the question
    let estimatedHeight = 40; // Base height for title and response count

    if (question.distribution && options.includeDetailedBreakdown) {
      estimatedHeight += 90; // Pie chart and table
    }
    if (question.numericStats && options.includeDetailedBreakdown) {
      estimatedHeight += 40; // Stats boxes
    }
    if (question.textResponses && question.textResponses.length > 0 && options.includeDetailedBreakdown) {
      estimatedHeight += Math.min(question.textResponses.length * 12, 80); // Text responses
    }

    // Force new page if question won't fit
    if (this.currentY + estimatedHeight > this.pageHeight - 30) {
      this.pdf.addPage();
      this.currentY = this.margin;
    }

    const cardStartY = this.currentY;

    // Question title with gradient-like background
    this.pdf.setFillColor('#EFF6FF'); // Light blue background
    const titleHeight = 16;
    this.pdf.rect(this.margin, cardStartY, this.pageWidth - 2 * this.margin, titleHeight, 'F');

    // Add left accent bar
    this.pdf.setFillColor(BRAND_COLORS.teal);
    this.pdf.rect(this.margin, cardStartY, 3, titleHeight, 'F');

    this.currentY = cardStartY + 6;

    this.pdf.setFont('helvetica', 'bold');
    this.pdf.setFontSize(11);
    this.pdf.setTextColor(BRAND_COLORS.primary);

    const titleLines = this.pdf.splitTextToSize(question.questionTitle, this.pageWidth - 2 * this.margin - 15);
    this.pdf.text(titleLines, this.margin + 8, this.currentY);
    this.currentY = cardStartY + titleHeight + 3;

    // Response count card - MODERN CARD DESIGN with skip rate
    this.addResponseCountCard(
      question.responseCount,
      question.totalSurveyRespondents,
      question.skipCount,
      question.eligibleRespondents ?? question.conditionalContext?.eligibleCount
    );
    this.currentY += 3;

    // Add detailed analytical distribution breakdown (website style)
    if (question.distribution && options.includeDetailedBreakdown) {
      this.checkPageBreak(20);
      this.addAnalyticalDistribution(question, options);
    }

    // Conditional context banner
    if (options.showConditionalContext && question.conditionalContext) {
      this.checkPageBreak(15);
      this.addConditionalBanner(question.conditionalContext, question.responseCount, question.totalSurveyRespondents);
    }

    // Distribution data with pie chart
    if (question.distribution && options.includeDetailedBreakdown) {
      this.checkPageBreak(30);
      await this.addDistributionWithChart(question, options);
    }

    // Numeric stats
    if (question.numericStats && options.includeDetailedBreakdown) {
      this.checkPageBreak(30);
      this.addNumericStats(question.numericStats);
    }

    // Text responses
    if (question.textResponses && question.textResponses.length > 0 && options.includeDetailedBreakdown) {
      this.checkPageBreak(30);
      this.addTextResponses(question.textResponses);
    }

    // Bottom padding
    this.currentY += 2;

    // Draw card border
    const cardHeight = this.currentY - cardStartY;
    this.pdf.setDrawColor('#E5E7EB');
    this.pdf.setLineWidth(0.5);
    this.pdf.rect(this.margin, cardStartY, this.pageWidth - 2 * this.margin, cardHeight);

    this.currentY += 5;
  }

  /**
   * Add conditional question banner
   */
  private addConditionalBanner(
    context: { isConditional: boolean; eligibleCount: number; description: string },
    responseCount: number,
    totalRespondents: number
  ): void {
    const bannerY = this.currentY;

    // Background
    this.pdf.setFillColor('#FEF3C7'); // Light yellow
    this.pdf.rect(this.margin + 5, bannerY, this.pageWidth - 2 * this.margin - 10, 10, 'F');

    // Icon and text
    this.pdf.setFontSize(8);
    this.pdf.setTextColor(BRAND_COLORS.muted);

    if (context.isConditional) {
      const text = `⚠ Conditional: ${context.description} | Shown to ${context.eligibleCount} of ${totalRespondents} respondents`;
      this.pdf.text(text, this.margin + 8, bannerY + 6);
    } else {
      const text = `✓ Standard Question | ${responseCount} of ${totalRespondents} answered`;
      this.pdf.text(text, this.margin + 8, bannerY + 6);
    }

    this.currentY += 12;
  }

  /**
   * Add detailed analytical distribution breakdown (website style)
   */
  private addAnalyticalDistribution(question: QuestionAnalytics, options: PDFExportOptions): void {
    if (!question.distribution) return;

    const entries = Object.entries(question.distribution).sort(([, a], [, b]) => b.count - a.count);

    // Header with background
    this.pdf.setFillColor('#F3F4F6');
    this.pdf.rect(this.margin + 5, this.currentY - 2, this.pageWidth - 2 * this.margin - 10, 8, 'F');

    this.pdf.setFontSize(9);
    this.pdf.setFont('helvetica', 'bold');
    this.pdf.setTextColor(BRAND_COLORS.primary);
    this.pdf.text(`Distribution (${question.responseCount} responses)`, this.margin + 8, this.currentY + 3);
    this.currentY += 10;

    // Display each option with detailed breakdown
    entries.forEach(([option, data]) => {
      this.checkPageBreak(18);

      const formattedOption = this.formatLabel(option);

      // Option value (bold, primary color)
      this.pdf.setFont('helvetica', 'bold');
      this.pdf.setFontSize(10);
      this.pdf.setTextColor(BRAND_COLORS.primary);
      this.pdf.text(formattedOption, this.margin + 10, this.currentY);
      this.currentY += 6;

      // Count (large, accent color)
      this.pdf.setFont('helvetica', 'bold');
      this.pdf.setFontSize(16);
      this.pdf.setTextColor(BRAND_COLORS.accent);
      this.pdf.text(data.count.toString(), this.margin + 12, this.currentY);
      this.currentY += 6;

      // Dual denominators with proper spacing
      this.pdf.setFontSize(8);
      this.pdf.setFont('helvetica', 'normal');
      this.pdf.setTextColor(BRAND_COLORS.muted);

      // First line - percentage of respondents
      const line1 = `   ${data.percentage.toFixed(1)}% of ${question.responseCount} who answered this question`;
      this.pdf.text(line1, this.margin + 12, this.currentY);
      this.currentY += 4;

      // Second line - percentage of total
      const line2 = `   ${data.percentageOfTotal.toFixed(1)}% of ${question.totalSurveyRespondents} total`;
      this.pdf.text(line2, this.margin + 12, this.currentY);
      this.currentY += 6;
    });

    // Add conditional/standard question info banner with background
    this.pdf.setFillColor('#EFF6FF');
    this.pdf.rect(this.margin + 5, this.currentY, this.pageWidth - 2 * this.margin - 10, 10, 'F');

    this.pdf.setFontSize(8);
    this.pdf.setFont('helvetica', 'normal');
    this.pdf.setTextColor(BRAND_COLORS.muted);

    const answeredPercentage = ((question.responseCount / question.totalSurveyRespondents) * 100).toFixed(1);

    if (question.conditionalContext?.isConditional) {
      this.pdf.text(`Conditional Question: ${question.conditionalContext.description}`, this.margin + 10, this.currentY + 4);
      this.currentY += 5;
      this.pdf.text(`${question.responseCount} of ${question.conditionalContext.eligibleCount} eligible answered (${answeredPercentage}%)`, this.margin + 10, this.currentY + 4);
      this.currentY += 8;
    } else {
      this.pdf.text('Standard Question: Shown to all respondents', this.margin + 10, this.currentY + 4);
      this.currentY += 5;
      this.pdf.text(`${question.responseCount} of ${question.totalSurveyRespondents} answered (${answeredPercentage}%)`, this.margin + 10, this.currentY + 4);
      this.currentY += 8;
    }
  }

  /**
   * Add distribution data with pie chart and table
   */
  private async addDistributionWithChart(question: QuestionAnalytics, options: PDFExportOptions): Promise<void> {
    if (!question.distribution) return;

    const entries = Object.entries(question.distribution).sort(([, a], [, b]) => b.count - a.count);

    // Add spacing before Response Distribution header
    this.currentY += 2;

    // Header
    this.pdf.setFontSize(10);
    this.pdf.setFont('helvetica', 'bold');
    this.pdf.setTextColor(BRAND_COLORS.primary);
    this.pdf.text('Response Distribution:', this.margin + 8, this.currentY);
    this.currentY += this.lineHeight + 4;

    // Draw pie chart if there are entries
    if (entries.length > 0 && entries.length <= 10) {
      const chartStartY = this.currentY;
      await this.drawPieChart(entries, this.margin + 10, this.currentY, 32);

      // Position for data table (next to pie chart)
      const tableX = this.margin + 80;
      let tableY = chartStartY;

      // Draw table header
      this.pdf.setFontSize(8);
      this.pdf.setFont('helvetica', 'bold');
      this.pdf.setTextColor(BRAND_COLORS.muted);
      this.pdf.text('Response', tableX, tableY);
      this.pdf.text('Count', tableX + 60, tableY);
      if (options.showPercentages) {
        this.pdf.text('%', tableX + 75, tableY);
      }
      tableY += 4;

      // Draw separator line
      this.pdf.setDrawColor(BRAND_COLORS.border);
      this.pdf.setLineWidth(0.3);
      this.pdf.line(tableX, tableY, this.pageWidth - this.margin - 8, tableY);
      tableY += 2.5;

      // Draw data table next to pie chart
      this.pdf.setFontSize(8);
      entries.forEach(([option, data], index) => {
        if (tableY > this.pageHeight - 40) return; // Prevent overflow

        // Color box matching pie chart
        const color = this.getPieChartColor(index);
        this.pdf.setFillColor(color);
        this.pdf.rect(tableX - 3.5, tableY - 2, 2.5, 2.5, 'F');

        // Option name - Format kebab-case to Title Case
        this.pdf.setFont('helvetica', 'normal');
        this.pdf.setTextColor(BRAND_COLORS.primary);
        const formattedOption = this.formatLabel(option);
        const optionText = this.truncateText(formattedOption, 28);
        this.pdf.text(optionText, tableX, tableY);

        // Count
        this.pdf.setFont('helvetica', 'bold');
        this.pdf.setTextColor(BRAND_COLORS.accent);
        this.pdf.text(`${data.count}`, tableX + 60, tableY, { align: 'right' });

        if (options.showPercentages) {
          this.pdf.setFont('helvetica', 'normal');
          this.pdf.setTextColor(BRAND_COLORS.muted);
          this.pdf.text(`${data.percentage.toFixed(1)}%`, tableX + 75, tableY, { align: 'right' });
        }

        tableY += this.lineHeight;

        // Dual denominators (if enabled) - IMPROVED READABILITY
        if (options.showDualDenominators) {
          this.pdf.setFontSize(7);
          this.pdf.setTextColor(BRAND_COLORS.muted);
          this.pdf.text(
            `${data.percentage.toFixed(1)}% of respondents | ${data.percentageOfTotal.toFixed(1)}% of total`,
            tableX,
            tableY
          );
          tableY += 3.5;
          this.pdf.setFontSize(8);
        }
      });

      this.currentY = Math.max(chartStartY + 75, tableY + 3); // Move past both chart and table
    } else {
      // Fallback to simple list if too many entries or none
      this.addDistributionTable(question, options);
    }
  }

  /**
   * Draw a pie chart for distribution data using simplified solid wedges
   */
  private async drawPieChart(
    entries: Array<[string, { count: number; percentage: number; percentageOfTotal: number }]>,
    x: number,
    y: number,
    radius: number
  ): Promise<void> {
    const total = entries.reduce((sum, [, data]) => sum + data.count, 0);
    if (total === 0) return;

    const centerX = x + radius;
    const centerY = y + radius;
    let currentAngle = -Math.PI / 2; // Start at top

    // Draw each slice as a filled wedge using ellipse arcs (native jsPDF support)
    entries.forEach(([, data], index) => {
      const sliceAngle = (data.count / total) * 2 * Math.PI;

      // Skip very small slices
      if (sliceAngle < 0.01) {
        currentAngle += sliceAngle;
        return;
      }

      // Get color for this slice
      const color = this.getPieChartColor(index);
      const [r, g, b] = this.hexToRgb(color);

      // Set fill color
      this.pdf.setFillColor(r, g, b);
      this.pdf.setDrawColor(r, g, b); // Match stroke to fill

      // Draw wedge using many small filled triangles
      const steps = Math.ceil(Math.abs(sliceAngle) * 50); // Very high resolution

      for (let i = 0; i < steps; i++) {
        const angle1 = currentAngle + (sliceAngle * i / steps);
        const angle2 = currentAngle + (sliceAngle * (i + 1) / steps);

        const x1 = centerX + radius * Math.cos(angle1);
        const y1 = centerY + radius * Math.sin(angle1);
        const x2 = centerX + radius * Math.cos(angle2);
        const y2 = centerY + radius * Math.sin(angle2);

        // Draw filled triangle with matching fill and stroke color
        this.pdf.triangle(centerX, centerY, x1, y1, x2, y2, 'FD');
      }

      currentAngle += sliceAngle;
    });

    // Draw clean white separation lines between slices
    currentAngle = -Math.PI / 2;
    this.pdf.setDrawColor(255, 255, 255); // White
    this.pdf.setLineWidth(1.5);

    entries.forEach(([, data]) => {
      const sliceAngle = (data.count / total) * 2 * Math.PI;

      if (sliceAngle >= 0.01) {
        const x1 = centerX + radius * Math.cos(currentAngle);
        const y1 = centerY + radius * Math.sin(currentAngle);
        this.pdf.line(centerX, centerY, x1, y1);
      }

      currentAngle += sliceAngle;
    });

    // Draw circle outline for clean look
    this.pdf.setDrawColor(55, 65, 81); // #374151 in RGB
    this.pdf.setLineWidth(0.5);
    this.pdf.circle(centerX, centerY, radius, 'S');
  }

  /**
   * Convert hex color to RGB values
   */
  private hexToRgb(hex: string): [number, number, number] {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
      ? [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)]
      : [0, 0, 0];
  }

  /**
   * Get color for pie chart slice
   */
  private getPieChartColor(index: number): string {
    const colors = [
      '#2B7A78', // Teal
      '#C7A14A', // Gold
      '#3B82F6', // Blue
      '#10B981', // Green
      '#F59E0B', // Orange
      '#EF4444', // Red
      '#8B5CF6', // Purple
      '#EC4899', // Pink
      '#14B8A6', // Cyan
      '#F97316', // Deep orange
    ];
    return colors[index % colors.length];
  }

  /**
   * Add distribution table with dual denominators (fallback/alternative view)
   */
  private addDistributionTable(question: QuestionAnalytics, options: PDFExportOptions): void {
    if (!question.distribution) return;

    const entries = Object.entries(question.distribution).sort(([, a], [, b]) => b.count - a.count);

    this.pdf.setFontSize(9);
    this.pdf.setFont('helvetica', 'bold');
    this.pdf.setTextColor(BRAND_COLORS.primary);
    this.pdf.text('Response Distribution:', this.margin + 8, this.currentY);
    this.currentY += this.lineHeight;

    entries.forEach(([option, data]) => {
      this.checkPageBreak(15);

      // Option name - Format kebab-case to Title Case
      this.pdf.setFont('helvetica', 'normal');
      this.pdf.setTextColor(BRAND_COLORS.primary);
      const formattedOption = this.formatLabel(option);
      const optionText = this.truncateText(formattedOption, 50);
      this.pdf.text(optionText, this.margin + 10, this.currentY);

      // Count
      this.pdf.setFont('helvetica', 'bold');
      this.pdf.setTextColor(BRAND_COLORS.accent);
      this.pdf.text(data.count.toString(), this.margin + 120, this.currentY);

      if (options.showPercentages) {
        this.pdf.setFont('helvetica', 'normal');
        this.pdf.setTextColor(BRAND_COLORS.muted);
        this.pdf.text(`(${data.percentage.toFixed(1)}%)`, this.margin + 130, this.currentY);
      }

      this.currentY += this.lineHeight;

      // Dual denominators (if enabled)
      if (options.showDualDenominators) {
        this.pdf.setFontSize(7);
        this.pdf.setTextColor(BRAND_COLORS.muted);
        this.pdf.text(
          `├─ ${data.percentage.toFixed(1)}% of ${question.responseCount} who answered`,
          this.margin + 12,
          this.currentY
        );
        this.currentY += 5;
        this.pdf.text(
          `└─ ${data.percentageOfTotal.toFixed(1)}% of ${question.totalSurveyRespondents} total`,
          this.margin + 12,
          this.currentY
        );
        this.currentY += 6;
        this.pdf.setFontSize(9);
      }
    });

    this.currentY += 3;
  }

  /**
   * Add numeric statistics with visual boxes
   */
  private addNumericStats(stats: { min: number; max: number; average: number; median: number; stdDev?: number; mode?: number }): void {
    this.pdf.setFontSize(10);
    this.pdf.setFont('helvetica', 'bold');
    this.pdf.setTextColor(BRAND_COLORS.primary);
    this.pdf.text('Statistical Summary:', this.margin + 8, this.currentY);
    this.currentY += this.lineHeight + 2;

    // Build stats array - include stdDev if available
    const statsData: Array<{ label: string; value: string; color: string }> = [
      { label: 'Average', value: stats.average.toFixed(2), color: BRAND_COLORS.teal },
      { label: 'Median', value: stats.median.toFixed(2), color: BRAND_COLORS.accent },
      { label: 'Minimum', value: stats.min.toString(), color: '#0F1B2D' },
      { label: 'Maximum', value: stats.max.toString(), color: '#2B7A78' },
    ];

    // Add standard deviation if available
    if (stats.stdDev !== undefined) {
      statsData.push({ label: 'Std Dev', value: stats.stdDev.toFixed(2), color: '#7C3AED' }); // Purple
    }

    // Add mode if available
    if (stats.mode !== undefined) {
      statsData.push({ label: 'Mode', value: stats.mode.toString(), color: '#059669' }); // Emerald
    }

    // Calculate grid layout
    const availableWidth = this.pageWidth - 2 * this.margin - 16;
    const cols = statsData.length <= 4 ? 2 : 3; // Use 3 columns if more than 4 stats
    const boxWidth = (availableWidth - (cols - 1) * 3) / cols;
    const boxHeight = 14;
    let xPos = this.margin + 8;
    let yPos = this.currentY;

    statsData.forEach((stat, index) => {
      if (index > 0 && index % cols === 0) {
        // Move to next row
        xPos = this.margin + 8;
        yPos += boxHeight + 2;
      }

      // Draw stat box
      this.pdf.setFillColor('#F9FAFB');
      this.pdf.rect(xPos, yPos, boxWidth, boxHeight, 'F');
      this.pdf.setDrawColor('#E5E7EB');
      this.pdf.setLineWidth(0.3);
      this.pdf.rect(xPos, yPos, boxWidth, boxHeight, 'S');

      // Add colored accent
      this.pdf.setFillColor(stat.color);
      this.pdf.rect(xPos, yPos, 2.5, boxHeight, 'F');

      // Label
      this.pdf.setFont('helvetica', 'normal');
      this.pdf.setFontSize(8);
      this.pdf.setTextColor(BRAND_COLORS.muted);
      this.pdf.text(stat.label, xPos + 4, yPos + 5.5);

      // Value
      this.pdf.setFont('helvetica', 'bold');
      this.pdf.setFontSize(13);
      this.pdf.setTextColor(BRAND_COLORS.primary);
      this.pdf.text(stat.value, xPos + 4, yPos + 11);

      xPos += boxWidth + 3;
    });

    this.currentY = yPos + boxHeight + 4;

    // Add range indicator bar
    this.addRangeIndicator(stats.min, stats.max, stats.average, stats.median);
  }

  /**
   * Add visual range indicator showing data spread
   */
  private addRangeIndicator(min: number, max: number, average: number, median: number): void {
    const barY = this.currentY;
    const barWidth = this.pageWidth - 2 * this.margin - 16;
    const barHeight = 8;

    // Background bar
    this.pdf.setFillColor('#E5E7EB');
    this.pdf.rect(this.margin + 8, barY, barWidth, barHeight, 'F');

    // Calculate positions for average and median markers
    const range = max - min;
    if (range > 0) {
      const avgPos = ((average - min) / range) * barWidth;
      const medPos = ((median - min) / range) * barWidth;

      // Average marker (teal line)
      this.pdf.setFillColor(BRAND_COLORS.teal);
      this.pdf.rect(this.margin + 8 + avgPos - 1, barY - 2, 2, barHeight + 4, 'F');

      // Median marker (gold line)
      this.pdf.setFillColor(BRAND_COLORS.accent);
      this.pdf.rect(this.margin + 8 + medPos - 1, barY, 2, barHeight, 'F');
    }

    // Labels
    this.pdf.setFontSize(6);
    this.pdf.setTextColor(BRAND_COLORS.muted);
    this.pdf.text(min.toString(), this.margin + 8, barY + barHeight + 4);
    this.pdf.text(max.toString(), this.margin + 8 + barWidth - 5, barY + barHeight + 4);

    // Legend
    this.pdf.setFontSize(6);
    const legendY = barY + barHeight + 8;
    this.pdf.setFillColor(BRAND_COLORS.teal);
    this.pdf.rect(this.margin + 8, legendY - 2, 3, 3, 'F');
    this.pdf.text('Avg', this.margin + 13, legendY);
    this.pdf.setFillColor(BRAND_COLORS.accent);
    this.pdf.rect(this.margin + 28, legendY - 2, 3, 3, 'F');
    this.pdf.text('Med', this.margin + 33, legendY);

    this.currentY = legendY + 5;
  }

  /**
   * Add text responses with better formatting
   */
  private addTextResponses(responses: string[]): void {
    this.pdf.setFontSize(10);
    this.pdf.setFont('helvetica', 'bold');
    this.pdf.setTextColor(BRAND_COLORS.primary);
    this.pdf.text('Text Responses:', this.margin + 8, this.currentY);

    this.pdf.setFont('helvetica', 'normal');
    this.pdf.setFontSize(8);
    this.pdf.setTextColor(BRAND_COLORS.muted);
    this.pdf.text(`(Showing ${Math.min(responses.length, 10)} of ${responses.length})`, this.margin + 45, this.currentY);
    this.currentY += this.lineHeight + 2;

    this.pdf.setFont('helvetica', 'normal');
    this.pdf.setFontSize(9);

    // Show up to 10 responses, or all if less
    const responsesToShow = responses.slice(0, 10);

    responsesToShow.forEach((response, index) => {
      this.checkPageBreak(15);

      const responseBoxY = this.currentY;

      // Light background for each response
      this.pdf.setFillColor('#FAFAFA');
      const responseText = response || '[No response]';
      const responseLines = this.pdf.splitTextToSize(responseText, this.pageWidth - 2 * this.margin - 20);
      const boxHeight = responseLines.length * (this.lineHeight - 0.5) + 3.5;

      this.pdf.rect(this.margin + 8, responseBoxY, this.pageWidth - 2 * this.margin - 16, boxHeight, 'F');

      // Response number badge
      this.pdf.setFillColor(BRAND_COLORS.teal);
      this.pdf.circle(this.margin + 13, responseBoxY + 4, 2.5, 'F');
      this.pdf.setFont('helvetica', 'bold');
      this.pdf.setFontSize(7);
      this.pdf.setTextColor('#FFFFFF');
      this.pdf.text(`${index + 1}`, this.margin + 11.5, responseBoxY + 5);

      // Response text
      this.pdf.setFont('helvetica', 'normal');
      this.pdf.setFontSize(9);
      this.pdf.setTextColor('#374151');
      this.pdf.text(responseLines, this.margin + 18, responseBoxY + 4.5);

      this.currentY += boxHeight + 1.5;
    });

    // If there are more responses, show count
    if (responses.length > 10) {
      this.pdf.setTextColor(BRAND_COLORS.accent);
      this.pdf.setFont('helvetica', 'italic');
      this.pdf.setFontSize(8);
      this.pdf.text(`+ ${responses.length - 10} additional response${responses.length - 10 > 1 ? 's' : ''} not shown`, this.margin + 10, this.currentY);
      this.currentY += this.lineHeight;
    }

    this.currentY += 3;
  }

  // ===== HELPER METHODS =====

  /**
   * Add modern card-based response context display with skip rate
   */
  private addResponseCountCard(responseCount: number, totalRespondents: number, skipCount?: number, eligibleRespondents?: number): void {
    const cardStartY = this.currentY;
    const cardHeight = 16;
    const cardWidth = this.pageWidth - 2 * this.margin - 16;

    // Card background
    this.pdf.setFillColor('#F0FDF4'); // Light green background
    this.pdf.rect(this.margin + 8, cardStartY, cardWidth, cardHeight, 'F');

    // Left section - Response count (teal background)
    this.pdf.setFillColor(BRAND_COLORS.teal);
    this.pdf.rect(this.margin + 8, cardStartY, 32, cardHeight, 'F');

    // Response count number (white on teal)
    this.pdf.setFont('helvetica', 'bold');
    this.pdf.setFontSize(16);
    this.pdf.setTextColor('#FFFFFF');
    this.pdf.text(responseCount.toString(), this.margin + 24, cardStartY + 10, { align: 'center' });

    // Calculate response rate
    const eligible = eligibleRespondents ?? totalRespondents;
    const responseRate = eligible > 0 ? ((responseCount / eligible) * 100).toFixed(1) : '0.0';

    // Response context text
    this.pdf.setFont('helvetica', 'bold');
    this.pdf.setFontSize(9);
    this.pdf.setTextColor(BRAND_COLORS.primary);
    this.pdf.text(`${responseCount} of ${eligible} responded (${responseRate}%)`, this.margin + 45, cardStartY + 7);

    // Skip count if available
    if (skipCount !== undefined && skipCount > 0) {
      this.pdf.setFont('helvetica', 'normal');
      this.pdf.setFontSize(8);
      this.pdf.setTextColor('#DC2626'); // Red for skipped
      const skipRate = eligible > 0 ? ((skipCount / eligible) * 100).toFixed(1) : '0.0';
      this.pdf.text(`${skipCount} skipped (${skipRate}%)`, this.margin + 45, cardStartY + 13);
    } else {
      this.pdf.setFont('helvetica', 'normal');
      this.pdf.setFontSize(8);
      this.pdf.setTextColor(BRAND_COLORS.muted);
      this.pdf.text(`out of ${totalRespondents} total survey respondents`, this.margin + 45, cardStartY + 13);
    }

    // Card border
    this.pdf.setDrawColor('#86EFAC'); // Light green border
    this.pdf.setLineWidth(0.5);
    this.pdf.rect(this.margin + 8, cardStartY, cardWidth, cardHeight);

    this.currentY += cardHeight + 3;
  }

  private addSectionHeader(title: string): void {
    this.checkPageBreak(20);

    // Accent line
    this.pdf.setFillColor(BRAND_COLORS.accent);
    this.pdf.rect(this.margin, this.currentY, 3, 8, 'F');

    this.pdf.setFont('helvetica', 'bold');
    this.pdf.setFontSize(16);
    this.pdf.setTextColor(BRAND_COLORS.primary);
    this.pdf.text(title, this.margin + 7, this.currentY + 6);

    this.currentY += 15;
  }

  private addSubheader(title: string): void {
    this.checkPageBreak(15);

    this.pdf.setFont('helvetica', 'bold');
    this.pdf.setFontSize(12);
    this.pdf.setTextColor(BRAND_COLORS.primary);
    this.pdf.text(title, this.margin, this.currentY);

    this.currentY += this.lineHeight + 2;
  }

  private addBodyText(text: string): void {
    this.pdf.setFont('helvetica', 'normal');
    this.pdf.setFontSize(10);
    this.pdf.setTextColor(BRAND_COLORS.muted);

    const lines = this.pdf.splitTextToSize(text, this.pageWidth - 2 * this.margin);
    this.pdf.text(lines, this.margin, this.currentY);

    this.currentY += lines.length * this.lineHeight;
  }

  private addInfoBox(items: Array<{ label: string; value: string }>): void {
    const boxHeight = items.length * 10 + 5;

    // Background
    this.pdf.setFillColor(BRAND_COLORS.surface);
    this.pdf.rect(this.margin, this.currentY, this.pageWidth - 2 * this.margin, boxHeight, 'F');

    // Border
    this.pdf.setDrawColor(BRAND_COLORS.border);
    this.pdf.setLineWidth(0.5);
    this.pdf.rect(this.margin, this.currentY, this.pageWidth - 2 * this.margin, boxHeight);

    this.currentY += 6;

    items.forEach(item => {
      this.pdf.setFont('helvetica', 'normal');
      this.pdf.setFontSize(10);
      this.pdf.setTextColor(BRAND_COLORS.muted);
      this.pdf.text(item.label + ':', this.margin + 5, this.currentY);

      this.pdf.setFont('helvetica', 'bold');
      this.pdf.setTextColor(BRAND_COLORS.primary);
      this.pdf.text(item.value, this.margin + 70, this.currentY);

      this.currentY += 10;
    });

    this.currentY += 2;
  }

  private addFooterToAllPages(options: PDFExportOptions): void {
    const pageCount = this.pdf.getNumberOfPages();

    for (let i = 1; i <= pageCount; i++) {
      this.pdf.setPage(i);

      // Footer line
      this.pdf.setDrawColor(BRAND_COLORS.accent);
      this.pdf.setLineWidth(0.5);
      this.pdf.line(this.margin, this.pageHeight - 15, this.pageWidth - this.margin, this.pageHeight - 15);

      // Footer text
      this.pdf.setFontSize(8);
      this.pdf.setTextColor(BRAND_COLORS.muted);
      this.pdf.text('Crisci Create - Pharmacy Research Platform', this.margin, this.pageHeight - 10);
      this.pdf.text(`Page ${i} of ${pageCount}`, this.pageWidth - this.margin - 20, this.pageHeight - 10);
    }
  }

  private checkPageBreak(requiredSpace: number): void {
    if (this.currentY + requiredSpace > this.pageHeight - 30) {
      this.pdf.addPage();
      this.currentY = this.margin;
    }
  }

  private truncateText(text: string, maxLength: number): string {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength - 3) + '...';
  }

  /**
   * Format option labels - convert kebab-case or snake_case to Title Case
   */
  private formatLabel(label: string): string {
    return label
      .split(/[-_]/)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  private async loadImage(url: string): Promise<string> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'Anonymous';
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx?.drawImage(img, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      };
      img.onerror = reject;
      img.src = url;
    });
  }
}
